import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import { FormProvider } from "react-hook-form";

import { cn } from "../../lib/utils";
import { FormField } from "./form-field";
import { FormItem } from "./form-item";
import { FormItemContext } from "./form-item-context";
import { Label } from "./label";
import { useFormField } from "./use-form-field";

const Form = FormProvider;

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      className={cn(error && "text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ children, ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();
  const { setControlId } = React.useContext(FormItemContext);
  const child = React.isValidElement<{ id?: unknown }>(children)
    ? children
    : null;
  const childId = child?.props.id;
  const explicitId =
    typeof childId === "string" && childId !== "" ? childId : undefined;
  // Slot lets the child's props win, so a child `id` replaces formItemId on
  // the rendered element. Publish it so FormLabel's htmlFor still matches -
  // otherwise the label names nothing (WCAG 1.3.1 / 4.1.2).
  React.useEffect(() => {
    if (!setControlId) return;
    setControlId(explicitId);
    return () => setControlId(undefined);
  }, [explicitId, setControlId]);
  // A wrapper that forwards an optional `id` as `id={undefined}` would erase
  // formItemId the same way and leave the control unlabelled; restore it.
  const control =
    child && explicitId === undefined
      ? React.cloneElement(child, { id: formItemId })
      : children;

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    >
      {control}
    </Slot>
  );
});
FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
});
FormDescription.displayName = "FormDescription";

type FormMessageProps = React.HTMLAttributes<HTMLParagraphElement> & {
  formatError?: (message: string) => React.ReactNode;
};

const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ className, children, formatError, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const message = error ? String(error.message) : undefined;
    const body = message ? (formatError?.(message) ?? message) : children;

    if (!body) {
      return null;
    }

    return (
      <p
        ref={ref}
        id={formMessageId}
        className={cn("text-sm font-medium text-destructive", className)}
        {...props}
      >
        {body}
      </p>
    );
  },
);
FormMessage.displayName = "FormMessage";

export {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
};
